import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ArrowLeftRight,
  CheckCircle2,
  RotateCcw,
} from "lucide-react";
import {
  Link,
  useParams,
} from "react-router-dom";
import {
  Card,
  EmptyState,
  InlineNotice,
  Money,
  PageHeader,
  StatusBadge,
} from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatDateTime } from "../lib/format";

interface ReturnItem {
  id: string;
  quantity: number;
  baseQuantity: number;
  amount: number;
}

interface SaleItem {
  id: string;
  productId: string;
  productNameSnapshot: string;
  unitSnapshot: string;
  unitMultiplier: number;
  quantity: number;
  baseQuantity: number;
  unitPrice: number;
  lineTotal: number;
  returnItems: ReturnItem[];
  lotAllocations: Array<{
    id: string;
    baseQuantity: number;
    lot: {
      id: string;
      lotCode?: string | null;
      expiryDate?: string | null;
    };
  }>;
}

interface SaleDetail {
  id: string;
  code: string;
  status:
    | "COMPLETED"
    | "PARTIALLY_REFUNDED"
    | "REFUNDED"
    | "VOIDED";
  total: number;
  paymentMethod:
    | "CASH"
    | "BANK_TRANSFER"
    | "DEBT";
  customerId?: string | null;
  note?: string | null;
  createdAt: string;
  items: SaleItem[];
  returns: Array<{
    id: string;
    code: string;
    total: number;
    reason: string;
    createdAt: string;
    items: ReturnItem[];
  }>;
}

function normalizeSale(
  sale: SaleDetail,
): SaleDetail {
  return {
    ...sale,
    total: Number(sale.total),
    items: sale.items.map((item) => ({
      ...item,
      unitMultiplier: Number(
        item.unitMultiplier,
      ),
      quantity: Number(item.quantity),
      baseQuantity: Number(
        item.baseQuantity,
      ),
      unitPrice: Number(item.unitPrice),
      lineTotal: Number(item.lineTotal),
      returnItems: item.returnItems.map(
        (returned) => ({
          ...returned,
          quantity: Number(
            returned.quantity,
          ),
          baseQuantity: Number(
            returned.baseQuantity,
          ),
          amount: Number(returned.amount),
        }),
      ),
      lotAllocations:
        item.lotAllocations.map(
          (allocation) => ({
            ...allocation,
            baseQuantity: Number(
              allocation.baseQuantity,
            ),
          }),
        ),
    })),
    returns: sale.returns.map(
      (returned) => ({
        ...returned,
        total: Number(returned.total),
        items: returned.items.map(
          (item) => ({
            ...item,
            quantity: Number(
              item.quantity,
            ),
            baseQuantity: Number(
              item.baseQuantity,
            ),
            amount: Number(item.amount),
          }),
        ),
      }),
    ),
  };
}

export function SaleDetailPage() {
  const { id } = useParams<{
    id: string;
  }>();
  const auth = useAuth();
  const [sale, setSale] =
    useState<SaleDetail | null>(null);
  const [loading, setLoading] =
    useState(true);
  const [error, setError] = useState("");
  const [showReturn, setShowReturn] =
    useState(false);
  const [reason, setReason] = useState("");
  const [quantities, setQuantities] =
    useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] =
    useState("");

  const canReturn =
    ["OWNER", "MANAGER"].includes(
      auth.session?.user.role ?? "",
    ) &&
    navigator.onLine &&
    sale &&
    !["REFUNDED", "VOIDED"].includes(
      sale.status,
    );

  async function load() {
    if (!id || !navigator.onLine) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result =
        await api<SaleDetail>(
          `/sales/${id}`,
        );
      setSale(normalizeSale(result));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể tải chi tiết đơn",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [id]);

  const returnable = useMemo(() => {
    if (!sale) return [];

    return sale.items.map((item) => {
      const returned =
        item.returnItems.reduce(
          (sum, line) =>
            sum + line.quantity,
          0,
        );
      return {
        item,
        returned,
        remaining:
          Math.round(
            (item.quantity -
              returned +
              Number.EPSILON) *
              1000,
          ) / 1000,
      };
    });
  }, [sale]);

  const returnTotal = useMemo(
    () =>
      returnable.reduce(
        (sum, line) =>
          sum +
          Math.round(
            (quantities[line.item.id] ??
              0) *
              line.item.unitPrice,
          ),
        0,
      ),
    [returnable, quantities],
  );

  function openReturn() {
    setReason("");
    setError("");
    setSuccess("");
    setQuantities(
      Object.fromEntries(
        returnable.map((line) => [
          line.item.id,
          0,
        ]),
      ),
    );
    setShowReturn(true);
  }

  async function submitReturn() {
    if (!sale || !id) return;

    const lines = returnable
      .map((line) => ({
        saleItemId: line.item.id,
        quantity:
          quantities[line.item.id] ?? 0,
        remaining: line.remaining,
      }))
      .filter(
        (line) => line.quantity > 0,
      );

    if (lines.length === 0) {
      setError(
        "Chọn ít nhất một mặt hàng cần hoàn.",
      );
      return;
    }

    if (
      lines.some(
        (line) =>
          line.quantity >
          line.remaining + 0.0005,
      )
    ) {
      setError(
        "Số lượng hoàn vượt quá số lượng còn có thể hoàn.",
      );
      return;
    }

    if (reason.trim().length < 3) {
      setError(
        "Cần ghi lý do hoàn hàng.",
      );
      return;
    }

    setBusy(true);
    setError("");

    try {
      const result = await api<{
        total: number;
        saleStatus: string;
      }>("/returns", {
        method: "POST",
        body: JSON.stringify({
          idempotencyKey:
            crypto.randomUUID(),
          saleId: id,
          reason: reason.trim(),
          lines: lines.map(
            ({
              saleItemId,
              quantity,
            }) => ({
              saleItemId,
              quantity,
            }),
          ),
        }),
      });

      setSuccess(
        `Đã hoàn ${Number(result.total).toLocaleString("vi-VN")} ₫. Tồn kho, tiền/công nợ và lịch sử đã được cập nhật bằng giao dịch đảo.`,
      );
      setShowReturn(false);
      await load();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể hoàn hàng",
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="page-stack">
        <PageHeader
          title="Chi tiết đơn"
          back
        />
        <Card className="loading-card">
          Đang tải giao dịch...
        </Card>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="page-stack">
        <PageHeader
          title="Chi tiết đơn"
          back
        />
        {error ? (
          <InlineNotice
            tone="danger"
            title={error}
          />
        ) : (
          <InlineNotice
            tone="warning"
            title="Cần kết nối mạng"
          >
            Chi tiết giao dịch và hoàn hàng
            cần dữ liệu authoritative từ server.
          </InlineNotice>
        )}
        <Link
          className="btn btn-secondary"
          to="/sales/history"
        >
          Về lịch sử bán hàng
        </Link>
      </div>
    );
  }

  const statusTone =
    sale.status === "COMPLETED"
      ? "success"
      : sale.status ===
          "PARTIALLY_REFUNDED"
        ? "warning"
        : "neutral";

  return (
    <div className="page-stack">
      <PageHeader
        title={sale.code}
        description={formatDateTime(
          sale.createdAt,
        )}
        back
        action={
          canReturn ? (
            <button
              className="btn btn-secondary"
              onClick={openReturn}
            >
              <RotateCcw size={18} /> Hoàn
              hàng
            </button>
          ) : undefined
        }
      />

      {success ? (
        <InlineNotice
          tone="success"
          title="Hoàn hàng thành công"
        >
          {success}
        </InlineNotice>
      ) : null}

      {error && !showReturn ? (
        <InlineNotice
          tone="danger"
          title={error}
        />
      ) : null}

      <Card className="sale-detail-summary">
        <div>
          <span>Trạng thái</span>
          <StatusBadge
            tone={statusTone}
          >
            {sale.status === "COMPLETED"
              ? "Hoàn tất"
              : sale.status ===
                  "PARTIALLY_REFUNDED"
                ? "Đã hoàn một phần"
                : sale.status ===
                    "REFUNDED"
                  ? "Đã hoàn toàn bộ"
                  : "Đã hủy"}
          </StatusBadge>
        </div>
        <div>
          <span>Thanh toán</span>
          <strong>
            {sale.paymentMethod === "CASH"
              ? "Tiền mặt"
              : sale.paymentMethod ===
                  "BANK_TRANSFER"
                ? "Chuyển khoản"
                : "Ghi nợ"}
          </strong>
        </div>
        <div>
          <span>Tổng đơn</span>
          <Money
            value={sale.total}
            className="money-large"
          />
        </div>
      </Card>

      <section>
        <div className="section-title">
          <div>
            <ArrowLeftRight size={20} />
            <h2>Hàng đã bán</h2>
          </div>
        </div>

        <Card className="sale-item-list">
          {returnable.map(
            ({
              item,
              returned,
              remaining,
            }) => (
              <div
                className="sale-item-row"
                key={item.id}
              >
                <div>
                  <strong>
                    {
                      item.productNameSnapshot
                    }
                  </strong>
                  <small>
                    {item.quantity}{" "}
                    {item.unitSnapshot} ×{" "}
                    {item.unitPrice.toLocaleString(
                      "vi-VN",
                    )}{" "}
                    ₫
                  </small>
                  {returned > 0 ? (
                    <small className="text-warning">
                      Đã hoàn {returned} · Còn có
                      thể hoàn {remaining}
                    </small>
                  ) : null}
                </div>
                <Money
                  value={item.lineTotal}
                />
              </div>
            ),
          )}
        </Card>
      </section>

      {sale.returns.length > 0 ? (
        <section>
          <div className="section-title">
            <div>
              <RotateCcw size={20} />
              <h2>Lịch sử hoàn</h2>
            </div>
          </div>

          <Card className="return-list">
            {sale.returns.map(
              (returned) => (
                <div
                  className="return-row"
                  key={returned.id}
                >
                  <div>
                    <strong>
                      {returned.code}
                    </strong>
                    <small>
                      {formatDateTime(
                        returned.createdAt,
                      )}{" "}
                      · {returned.reason}
                    </small>
                  </div>
                  <Money
                    value={returned.total}
                  />
                </div>
              ),
            )}
          </Card>
        </section>
      ) : null}

      {!canReturn &&
      !["REFUNDED", "VOIDED"].includes(
        sale.status,
      ) ? (
        <InlineNotice
          tone="info"
          title="Hoàn hàng là thao tác nhạy cảm"
        >
          Chỉ Chủ cửa hàng hoặc Quản lý đang
          online mới được tạo giao dịch hoàn.
        </InlineNotice>
      ) : null}

      {showReturn ? (
        <div
          className="modal-backdrop"
          onMouseDown={() =>
            !busy &&
            setShowReturn(false)
          }
        >
          <div
            className="bottom-sheet"
            role="dialog"
            aria-modal="true"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >
            <div className="sheet-handle" />
            <h2>Hoàn hàng</h2>

            <InlineNotice
              tone="warning"
              title="Không sửa đơn cũ"
            >
              Hệ thống tạo giao dịch đảo để
              trả tồn kho và hoàn tiền/giảm
              công nợ, giữ nguyên lịch sử gốc.
            </InlineNotice>

            {error ? (
              <InlineNotice
                tone="danger"
                title={error}
              />
            ) : null}

            <div className="return-editor">
              {returnable
                .filter(
                  (line) =>
                    line.remaining > 0,
                )
                .map((line) => (
                  <label
                    key={line.item.id}
                  >
                    <span>
                      {
                        line.item
                          .productNameSnapshot
                      }{" "}
                      · tối đa{" "}
                      {line.remaining}{" "}
                      {
                        line.item
                          .unitSnapshot
                      }
                    </span>
                    <input
                      type="number"
                      min="0"
                      max={
                        line.remaining
                      }
                      step="0.001"
                      value={
                        quantities[
                          line.item.id
                        ] || ""
                      }
                      onChange={(event) =>
                        setQuantities(
                          (current) => ({
                            ...current,
                            [line.item.id]:
                              Number(
                                event.target
                                  .value,
                              ) || 0,
                          }),
                        )
                      }
                    />
                  </label>
                ))}
            </div>

            <label>
              Lý do hoàn
              <textarea
                rows={3}
                value={reason}
                onChange={(event) =>
                  setReason(
                    event.target.value,
                  )
                }
                placeholder="Ví dụ: Khách đổi ý, hàng còn nguyên"
              />
            </label>

            <div className="return-total">
              <span>
                Số tiền xử lý
              </span>
              <Money
                value={returnTotal}
                className="money-large"
              />
            </div>

            <button
              className="btn btn-primary btn-lg"
              disabled={
                busy ||
                returnTotal <= 0 ||
                reason.trim().length < 3
              }
              onClick={() =>
                void submitReturn()
              }
            >
              <CheckCircle2 size={20} />{" "}
              {busy
                ? "Đang xử lý..."
                : "Xác nhận hoàn hàng"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
